import { useState } from 'react';
import { Button } from '@finpay-development/shared-components';
import {
  Grid,
  Typography,
  Button as MUIButton,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { formatNumberToUSD, calculateDownByPercent, calculateTermPmt } from 'src/shared/calculators';
import { showErrorStatus } from 'src/security/state/user-slice';
import type { PaymentProgramViewContext } from './use-payment-program-view';

interface PreauthorizedPaymentViewProps {
  ctx: PaymentProgramViewContext;
}

export function PreauthorizedPaymentView({ ctx }: PreauthorizedPaymentViewProps) {
  const [selectedProgram, setSelectedProgram] = useState<string>('');

  const {
    matchingRule,
    pfrAmt,
    optDownPayment,
    billingFrequency,
    isLoadingEncounter,
    isLoadingEvaluateRule,
    handleBack,
    handleSelectPreauthorized,
    handleSelectOther,
    dispatch,
  } = ctx;

  const isWeekly = billingFrequency === 'weekly';

  const buildProgramOptions = () => {
    if (!matchingRule || !pfrAmt || pfrAmt === 0) return [];
    if (!matchingRule.availableTerms || matchingRule.availableTerms.trim() === '') return [];

    const terms = matchingRule.availableTerms.split(',').map((t: string) => t.trim()).filter((t: string) => t);
    if (terms.length === 0) return [];

    const minimumDownPmt =
      matchingRule.minDownPmtType === '%'
        ? (matchingRule.minDownPmt || 0) / 100
        : matchingRule.minDownPmt || 0;
    const optimalDownPmt =
      matchingRule.optimalDownPmtType === '%'
        ? (matchingRule.optimalDownPmt || 0) / 100
        : matchingRule.optimalDownPmt || 0;

    const options: Array<{
      id: string;
      term: number;
      downPaymentType: 'Min' | 'Opt';
      downPayment: number;
      recurring: number;
      isOptimal: boolean;
    }> = [];

    terms.forEach((term: string, index: number) => {
      const termNum = parseInt(term);
      if (isNaN(termNum) || termNum <= 0) return;

      const minDownPmt =
        matchingRule.minDownPmtType === '%'
          ? calculateDownByPercent(minimumDownPmt, pfrAmt)
          : minimumDownPmt || 0;
      const effectiveTermNum = isWeekly ? termNum * 4 : termNum;
      const minRecurring = isWeekly
        ? (pfrAmt - minDownPmt) / effectiveTermNum
        : calculateTermPmt(pfrAmt, minDownPmt, termNum);
      options.push({
        id: `min-${index}`,
        term: termNum,
        downPaymentType: 'Min',
        downPayment: minDownPmt,
        recurring: minRecurring,
        isOptimal: false,
      });

      const optDownPmt =
        matchingRule.optimalDownPmtType === '%'
          ? calculateDownByPercent(optimalDownPmt, pfrAmt)
          : optimalDownPmt || 0;
      const optRecurring = isWeekly
        ? (pfrAmt - optDownPmt) / effectiveTermNum
        : calculateTermPmt(pfrAmt, optDownPmt, termNum);
      const optimalTermNum = matchingRule.optimalTerm ? parseInt(matchingRule.optimalTerm, 10) : 0;
      options.push({
        id: `opt-${index}`,
        term: termNum,
        downPaymentType: 'Opt',
        downPayment: optDownPmt,
        recurring: optRecurring,
        isOptimal: optimalTermNum === termNum,
      });
    });

    return options;
  };

  const programOptions = buildProgramOptions();

  const handlePayWithPlan = () => {
    if (!selectedProgram) {
      dispatch(showErrorStatus('Please select a payment program option'));
      return;
    }
    const selectedOption = programOptions.find((opt) => opt.id === selectedProgram);
    const downPaymentAmt = selectedOption?.downPayment ?? optDownPayment ?? 0;
    const selectedTerm = selectedOption?.term;
    handleSelectPreauthorized(downPaymentAmt, selectedTerm);
  };

  const isRuleValid =
    matchingRule &&
    matchingRule.ruleId !== undefined &&
    matchingRule.ruleId !== null &&
    matchingRule.ruleId > 0 &&
    pfrAmt > 0 &&
    matchingRule.optimalTerm &&
    matchingRule.availableTerms &&
    matchingRule.availableTerms.trim() !== '' &&
    programOptions.length > 0;

  const isLoading = isLoadingEncounter || isLoadingEvaluateRule;

  return (
    <Grid item xs={12}>
      <Grid container className="mt-4" spacing={2}>
        {isLoading ? (
          <Grid item xs={12}>
            <Typography variant="body1" textAlign="center">
              Loading payment program options...
            </Typography>
          </Grid>
        ) : isRuleValid ? (
          <>
            <Grid container direction="row" sx={{ borderBottom: '1px solid #e0e0e0', pb: 1, mb: 2 }}>
              <Grid item xs={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Terms
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Total Down Payment
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Recurring
                </Typography>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <RadioGroup
                value={selectedProgram}
                onChange={(e) => setSelectedProgram(e.target.value)}
                test-id="payment-program-preauthorized-options"
              >
                {programOptions.map((option) => (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    control={<Radio />}
                    label={
                      <Box
                        sx={{
                          border: option.isOptimal ? '2px solid #4caf50' : '1px solid #e0e0e0',
                          borderRadius: 1,
                          p: 1.5,
                          width: '100%',
                          backgroundColor: option.isOptimal ? '#f1f8f4' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Grid container direction="row" alignItems="center">
                          <Grid item xs={2}>
                            <Typography variant="body1">
                              {isWeekly ? option.term * 4 : option.term}
                            </Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography variant="body1">
                              {option.downPaymentType} {formatNumberToUSD(option.downPayment)}
                            </Typography>
                          </Grid>
                          <Grid item xs={5}>
                            <Typography variant="body1">
                              {formatNumberToUSD(option.recurring)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    }
                    sx={{
                      m: 0,
                      mb: 1,
                      width: '100%',
                      '& .MuiFormControlLabel-label': {
                        width: '100%',
                        ml: 1,
                      },
                    }}
                  />
                ))}
              </RadioGroup>
            </Grid>

            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} md={2} className="mt-2">
                <MUIButton
                  onClick={handleBack}
                  variant="outlined"
                  fullWidth
                  test-id="payment-program-preauthorized-back"
                  sx={{
                    backgroundColor: 'white',
                    borderColor: '#e0e0e0',
                    color: '#000',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#bdbdbd',
                    },
                  }}
                >
                  <ArrowBackIosIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> Back
                </MUIButton>
              </Grid>
              <Grid item xs={12} md={5} className="mt-2">
                <Button onClick={handlePayWithPlan} fullWidth test-id="payment-program-preauthorized-add-payment">
                  Add Payment Details
                </Button>
              </Grid>
              <Grid item xs={12} md={5} className="mt-2">
                <MUIButton onClick={handleSelectOther} fullWidth variant="text" sx={{ textTransform: 'none' }}>
                  Can't afford?
                </MUIButton>
              </Grid>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Typography variant="body1" textAlign="center">
              No Preauthorized Payment Options exist for Payment Program and Values Entered
            </Typography>
            {matchingRule && (
              <Typography variant="body2" textAlign="center" sx={{ mt: 1, color: 'text.secondary' }}>
                Debug: ruleId={matchingRule.ruleId}, availableTerms={matchingRule.availableTerms || 'none'},
                pfrAmt={pfrAmt}
              </Typography>
            )}
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
